import { areHostile } from '../../../shared/creatureDisposition';
import { benefitsFromInvisible } from '../../../shared/rules/effects/invisibility.mjs';
import { ADJACENT_ENEMY_SOURCE, LONG_RANGE_SOURCE, heavyWeaponDisadvantage } from '../../../shared/rules/weaponRange.mjs';
import { coverOf, creatureCover, layoutBlocksSight, layoutCover, mostProtectiveCover, segmentCrossesBox } from '../../../shared/rules/cover.mjs';
import { wallsBetween } from '../../../shared/rules/effects/spellWalls.mjs';
import { spellArea, spellAreaSquares } from '../../../shared/rules/effects/spellAreaGeometry.mjs';
import { normalizeLayout } from '../../../shared/mapLayout.mjs';
import { frightenedEffects } from '../../../shared/rules/frightened.mjs';
import { auraCover } from '../../../shared/rules/effects/auras.mjs';
import { calculateAbilityScore } from './derivedStats';
import { getFootprintSquares, getMapGridDimensions, minimumFootprintDistanceFeet, tokenAnchorCell } from './pushGeometry';

const INCAPACITATING = new Set(['condition:incapacitated', 'condition:paralyzed', 'condition:petrified', 'condition:stunned', 'condition:unconscious']);

/**
 * 2024 ranged attack rolls have Disadvantage while an enemy within 5 feet can see the attacker and isn't Incapacitated.
 * Enemies are the map's other creatures that the shared disposition rule calls hostile.
 */
export const hasAdjacentEnemy = ({ attackerId, attacker, combatState, spatialContext }) => {
  const tokens = spatialContext?.tokens || {};
  const entities = spatialContext?.entities || {};
  const source = tokens[attackerId];
  if (!source) return false;
  const self = entities[attackerId] || attacker || source;
  const effects = Array.isArray(combatState?.activeEffects) ? combatState.activeEffects : [];
  return Object.entries(tokens).some(([id, token]) => {
    if (id === String(attackerId) || !token) return false;
    const other = { ...token, ...(entities[id] || {}) };
    if (!areHostile(self, other)) return false;
    const hp = Number(other.currentHp);
    if (Number.isFinite(hp) && hp <= 0) return false;
    if (effects.some((effect) => String(effect?.targetCombatantId) === id && INCAPACITATING.has(effect.definitionId))) return false;
    if (benefitsFromInvisible(combatState, attackerId, id)) return false; // It cannot see the attacker.
    const distance = minimumFootprintDistanceFeet({ sourceToken: source, targetToken: token, sourceSize: self?.size || source.size,
      targetSize: other.size, map: spatialContext?.map });
    return distance !== null && distance <= 5;
  });
};

/** Range, adjacency and Heavy Disadvantage sources for a weapon or spell attack prepared by the resolver. */
export const getWeaponRangeDisadvantages = ({ attackerId, attack, attacker, combatState, spatialContext }) => {
  const sources = [];
  if (attack?.longRange) sources.push(LONG_RANGE_SOURCE);
  if (attack?.attackMode === 'ranged' && hasAdjacentEnemy({ attackerId, attacker, combatState, spatialContext })) sources.push(ADJACENT_ENEMY_SOURCE);
  // Monster stat blocks already account for their weapons; Heavy applies to characters.
  // A record without the relevant score (older or partial data) is not penalized.
  const score = (key) => (attacker?.[key] == null || attacker[key] === '' ? null : calculateAbilityScore(attacker, key));
  if (attack?.kind === 'weapon' && attacker && attacker.entityType !== 'enemy') {
    const heavy = heavyWeaponDisadvantage({ weapon: attack.weapon, strength: score('str') ?? 13, dexterity: score('dex') ?? 13 });
    if (heavy) sources.push(heavy);
  }
  return sources;
};

/**
 * 2024 Half Cover from creatures on the map between the attacker and the target (+2 AC), or null. Dead monsters are
 * remains, not creatures; a character at 0 Hit Points is still a creature in the way.
 */
export const getCreatureCover = ({ attackerId, targetId, spatialContext }) => {
  const tokens = spatialContext?.tokens || {};
  const entities = spatialContext?.entities || {};
  const map = spatialContext?.map;
  const spaceOf = (id) => {
    const token = tokens[id];
    if (!token) return null;
    const anchor = tokenAnchorCell(token, map);
    const other = { ...token, ...(entities[id] || {}) };
    return anchor ? { id, x: anchor.x, y: anchor.y, squares: getFootprintSquares(other.temporarySize || other.size), entity: other } : null;
  };
  const attacker = spaceOf(String(attackerId));
  const target = spaceOf(String(targetId));
  if (!attacker || !target) return null;
  const others = Object.keys(tokens).filter((id) => id !== String(attackerId) && id !== String(targetId)).map(spaceOf)
    .filter((space) => space && !(space.entity.entityType === 'enemy' && Number(space.entity.currentHp) <= 0));
  return creatureCover({ attacker, target, others });
};

// A map's drawn layout, cleaned once per layout object (the map stores what the DM's Map Maker saved).
const cleanLayouts = new WeakMap();
const mapLayoutOf = (map) => {
  const raw = map?.layout;
  if (!raw || typeof raw !== 'object') return null;
  if (!cleanLayouts.has(raw)) cleanLayouts.set(raw, normalizeLayout(raw));
  return cleanLayouts.get(raw);
};

/**
 * 2024 cover for an attack: Half Cover from creatures in the way, and Half, Three-Quarters or Total Cover from the
 * walls and stamped objects of the map's drawn layout, whichever is most protective, or null. Token positions are
 * scaled from the map's grid to the layout's, so a map shown at another grid size still lines up.
 */
// The attacker's and target's places on the map's drawn layout (centers and squares, in layout units), or null.
const layoutPlaces = ({ attackerId, targetId, spatialContext }) => {
  const layout = mapLayoutOf(spatialContext?.map);
  if (!layout) return null;
  const tokens = spatialContext?.tokens || {};
  const entities = spatialContext?.entities || {};
  const map = spatialContext?.map;
  const { columns, rows } = getMapGridDimensions(map);
  const scaleX = layout.columns / (columns || layout.columns);
  const scaleY = layout.rows / (rows || layout.rows);
  const spaceOf = (id) => {
    const token = tokens[id];
    const anchor = token && tokenAnchorCell(token, map);
    if (!anchor) return null;
    const other = { ...token, ...(entities[id] || {}) };
    return { anchor, squares: getFootprintSquares(other.temporarySize || other.size) };
  };
  const attacker = spaceOf(String(attackerId));
  const target = spaceOf(String(targetId));
  if (!attacker || !target) return null;
  const center = ({ anchor, squares }) => ({ x: (anchor.x + squares / 2) * scaleX, y: (anchor.y + squares / 2) * scaleY });
  const cells = ({ anchor, squares }) => Array.from({ length: squares * squares }, (_, i) => ({
    x: Math.floor((anchor.x + (i % squares)) * scaleX), y: Math.floor((anchor.y + Math.floor(i / squares)) * scaleY) }));
  return { layout, from: center(attacker), to: center(target), attackerCells: cells(attacker), targetCells: cells(target) };
};

/**
 * Whether `viewerId` has `targetId` in line of sight on the map: both are placed, no Heavily Obscured square of the
 * drawn layout blocks sight between them, and no drawn wall gives Total Cover. Without a layout, placed is enough.
 */
export const hasLineOfSight = ({ viewerId, targetId, spatialContext }) => {
  const tokens = spatialContext?.tokens || {};
  if (!tokens[String(viewerId)] || !tokens[String(targetId)]) return false;
  const places = layoutPlaces({ attackerId: viewerId, targetId, spatialContext });
  if (!places) return true;
  const cells = [...places.attackerCells, ...places.targetCells];
  if (layoutBlocksSight({ layout: places.layout, from: places.from, to: places.to, cells })) return false;
  return layoutCover({ layout: places.layout, from: places.from, to: places.to, ignoreCells: cells, attackerCells: places.attackerCells })?.degree !== 'total';
};

/** Frightened (2024): Disadvantage on attack rolls while a source of the attacker's fear is within line of sight. */
export const getFrightenedAttackSources = ({ attackerId, attacker, combatState, spatialContext }) => frightenedEffects(attacker, combatState, attackerId)
  .filter((effect) => !spatialContext?.tokens || hasLineOfSight({ viewerId: attackerId, targetId: effect.sourceCombatantId, spatialContext }))
  .map((effect) => `Frightened of ${spatialContext?.entities?.[String(effect.sourceCombatantId)]?.label || spatialContext?.entities?.[String(effect.sourceCombatantId)]?.name || effect.sourceName || 'a creature'}`);

// `combatState` adds Half Cover from Smite of Protection while the target is in that Paladin's aura. With `doorOpen`, the
// map's doors are open (the attacker said so).
export const getAttackCover = ({ attackerId, targetId, spatialContext, combatState, doorOpen = false }) => {
  const creature = mostProtectiveCover(getCreatureCover({ attackerId, targetId, spatialContext }), auraCover(combatState, targetId), spellWallCover({ attackerId, targetId, spatialContext, combatState }));
  const places = layoutPlaces({ attackerId, targetId, spatialContext });
  if (!places) return creature;
  const { layout, from, to, attackerCells, targetCells } = places;
  const fromLayout = layoutCover({ layout, from, to, ignoreCells: [...attackerCells, ...targetCells], attackerCells, openDoors: doorOpen });
  return mostProtectiveCover(creature, fromLayout);
};

/** Whether only a door gives the target Total Cover, so whether the door is open decides if it can be targeted. */
export const attackThroughDoor = (input) => getAttackCover(input)?.degree === 'total'
  && getAttackCover({ ...input, doorOpen: true })?.degree !== 'total';

// A creature's place in map squares, for spell areas and walls.
const mapSpace = (spatialContext, id) => {
  const token = spatialContext?.tokens?.[String(id)], anchor = token && tokenAnchorCell(token, spatialContext.map);
  if (!anchor) return null;
  const other = { ...token, ...(spatialContext.entities?.[String(id)] || {}) };
  const squares = getFootprintSquares(other.temporarySize || other.size);
  return { center: { x: anchor.x + squares / 2, y: anchor.y + squares / 2 }, cells: Array.from({ length: squares * squares }, (_, i) => `${anchor.x + (i % squares)},${anchor.y + Math.floor(i / squares)}`) };
};
const wallsOnLine = ({ attackerId, targetId, spatialContext, combatState }) => {
  if (!(combatState?.activeEffects || []).some((effect) => effect?.kind === 'spell-wall')) return [];
  const attacker = mapSpace(spatialContext, attackerId), target = mapSpace(spatialContext, targetId);
  return attacker && target ? wallsBetween(combatState.activeEffects, spatialContext.map?.mapId, attacker.center, target.center) : [];
};
const spellWallCover = (input) => mostProtectiveCover(...wallsOnLine(input).filter((wall) => wall.cover).map((wall) => coverOf(wall.cover, wall.name)));
/** Wind Wall: arrows, bolts and thrown weapons miss across it, and monsters' shots unless they're hurled rocks. */
export const getDeflectingWall = ({ attack, attackerId, targetId, spatialContext, combatState }) => {
  if (attack?.spell || attack?.kind === 'spell' || attack?.kind === 'cantrip' || attack?.isMeleeAttack === true || attack?.attackMode === 'melee' || /boulder|rock/i.test(String(attack?.name || ''))) return null;
  return wallsOnLine({ attackerId, targetId, spatialContext, combatState }).find((wall) => wall.deflectsMissiles) || null;
};

/** Spells that Heavily Obscure their area (Fog Cloud, Darkness, Cloudkill), and opaque walls: the same test as drawn fog, in map squares. */
const spellObscuredSources = ({ attackerId, targetId, spatialContext, combatState }) => {
  const map = spatialContext?.map;
  const effects = (combatState?.activeEffects || []).filter((effect) => effect?.terrain?.obscurement === 'heavy' && effect.placement?.mapId === map?.mapId);
  const walls = wallsOnLine({ attackerId, targetId, spatialContext, combatState }).filter((wall) => wall.opaque).map((wall) => wall.name);
  const attacker = effects.length ? mapSpace(spatialContext, attackerId) : null, target = effects.length ? mapSpace(spatialContext, targetId) : null;
  if (!attacker || !target) return [...new Set(walls)];
  const own = new Set([...attacker.cells, ...target.cells]);
  return [...new Set([...walls, ...effects.filter((effect) => {
    const geometry = spellArea({ area: effect.area, point: effect.placement.point, map });
    return geometry && spellAreaSquares({ geometry, map }).some(({ x, y }) => own.has(`${x},${y}`) || segmentCrossesBox(attacker.center, target.center, { x0: x + .05, y0: y + .05, x1: x + .95, y1: y + .95 }));
  }).map((effect) => effect.name)])];
};

/**
 * 2024 Heavily Obscured areas drawn on the map (fog, thick foliage) between or around the attacker and the target. Such
 * an area blocks sight entirely, so neither sees the other: the attacker has Disadvantage (it can't see the target) and
 * Advantage (the target can't see it), which cancel to a normal roll unless something else tips it.
 */
export const getObscuredAttackSources = ({ attackerId, targetId, spatialContext, combatState }) => {
  const places = layoutPlaces({ attackerId, targetId, spatialContext });
  const blocked = places && layoutBlocksSight({ layout: places.layout, from: places.from, to: places.to,
    cells: [...places.attackerCells, ...places.targetCells] });
  const sources = [...(blocked?.sources || []), ...spellObscuredSources({ attackerId, targetId, spatialContext, combatState })];
  if (!sources.length) return { advantageSources: [], disadvantageSources: [] };
  const what = sources.join(', ');
  return { advantageSources: [`Target can't see the attacker (${what})`], disadvantageSources: [`Can't see the target (${what})`] };
};
